import { useCallback, useRef, useEffect } from 'react'
import { Howl } from 'howler'

// Sound effect URLs - using reliable CDN-hosted sounds
// These are short, optimized MP3s for web use
const SOUNDS = {
  correct: 'https://assets.mixkit.co/active_storage/sfx/2000/2000-preview.mp3', // Success chime
  wrong: 'https://assets.mixkit.co/active_storage/sfx/2003/2003-preview.mp3',   // Gentle error
  celebration: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3', // Victory fanfare
  click: 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3',   // Soft click
  pop: 'https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3',     // Pop sound
  coin: 'https://assets.mixkit.co/active_storage/sfx/2001/2001-preview.mp3',     // Coin collect
  sparkle: 'https://assets.mixkit.co/active_storage/sfx/2578/2578-preview.mp3', // Magic sparkle
} as const

type SoundType = keyof typeof SOUNDS

export function useAudio() {
  const soundsRef = useRef<Record<SoundType, Howl | null>>({
    correct: null,
    wrong: null,
    celebration: null,
    click: null,
    pop: null,
    coin: null,
    sparkle: null,
  })

  // Initialize sounds on first use
  useEffect(() => {
    const sounds = soundsRef.current
    
    Object.entries(SOUNDS).forEach(([key, url]) => {
      sounds[key as SoundType] = new Howl({
        src: [url],
        volume: 0.5,
        preload: true,
        html5: true, // Force HTML5 Audio for better mobile support
      })
    })

    return () => {
      // Cleanup
      Object.values(sounds).forEach(sound => {
        if (sound) {
          sound.unload()
        }
      })
    }
  }, [])

  const play = useCallback((type: SoundType) => {
    const sound = soundsRef.current[type]
    if (sound) {
      // Stop if already playing to prevent overlap
      sound.stop()
      sound.play()
    }
  }, [])

  const playCorrect = useCallback(() => play('correct'), [play])
  const playWrong = useCallback(() => play('wrong'), [play])
  const playCelebration = useCallback(() => play('celebration'), [play])
  const playClick = useCallback(() => play('click'), [play])
  const playCoin = useCallback(() => play('coin'), [play])
  const playSparkle = useCallback(() => play('sparkle'), [play])

  return {
    play,
    playCorrect,
    playWrong,
    playCelebration,
    playClick,
    playCoin,
    playSparkle,
  }
}

export default useAudio
